import React, { useState, useEffect, useCallback } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import SidebarLayout from "./SidebarLayout";
import StaffSidebarLayout from "../staff/StaffSidebarLayout";
import FreelancerSidebarLayout from "../freelancer/FreelancerSidebarLayout";
import "./shopStyles.css";

function Contacts() {
  const location = useLocation();
  const navigate = useNavigate();
  const [contacts, setContacts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [searchTerm, setSearchTerm] = useState("");
  const [selectedContact, setSelectedContact] = useState(null);
  const [viewMode, setViewMode] = useState("grid"); // 'grid' or 'list'
  const [showAddModal, setShowAddModal] = useState(false);
  const [formData, setFormData] = useState({
    firstName: "",
    lastName: "",
    email: "",
    phone: "",
    notes: "",
  });
  const [formError, setFormError] = useState("");
  const [formSuccess, setFormSuccess] = useState("");
  const [submitting, setSubmitting] = useState(false);

  // Determine which layout to use based on route
  const isProfessionalPortal = location.pathname.includes(
    "/dashboard/professional",
  );
  const isBoothRenterPortal = location.pathname.includes(
    "/dashboard/booth-renter",
  );
  const isFreelancerPortal =
    location.pathname.includes("/dashboard/freelancer") || isBoothRenterPortal;
  const LayoutComponent = isFreelancerPortal
    ? FreelancerSidebarLayout
    : isProfessionalPortal
      ? StaffSidebarLayout
      : SidebarLayout;

  const fetchContacts = useCallback(async () => {
    try {
      setLoading(true);
      const token = localStorage.getItem("ogestyle_token");

      const response = await fetch(
        `${process.env.REACT_APP_API_BASE_URL || ""}/api/customers`,
        {
          headers: {
            Authorization: `Bearer ${token}`,
            "Content-Type": "application/json",
          },
        },
      );

      if (!response.ok) {
        const data = await response.json();
        throw new Error(data.message || "Failed to fetch contacts");
      }

      const data = await response.json();
      setContacts(data.contacts || []);
    } catch (err) {
      setError(err.message || "Failed to load contacts");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchContacts();
  }, [fetchContacts]);

  // Handle add customer form
  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
    setFormError("");
  };

  const handleAddCustomer = async (e) => {
    e.preventDefault();
    setFormError("");
    setFormSuccess("");

    // Validate
    if (!formData.firstName.trim() || !formData.lastName.trim()) {
      setFormError("First name and last name are required");
      return;
    }

    if (!formData.email.trim() && !formData.phone.trim()) {
      setFormError("Either email or phone is required");
      return;
    }

    try {
      setSubmitting(true);
      const token = localStorage.getItem("ogestyle_token");

      const response = await fetch(
        `${process.env.REACT_APP_API_BASE_URL || ""}/api/customers`,
        {
          method: "POST",
          headers: {
            Authorization: `Bearer ${token}`,
            "Content-Type": "application/json",
          },
          body: JSON.stringify(formData),
        },
      );

      const data = await response.json();

      if (!response.ok) {
        throw new Error(data.message || "Failed to create customer");
      }

      setFormSuccess("Customer added successfully!");
      setFormData({
        firstName: "",
        lastName: "",
        email: "",
        phone: "",
        notes: "",
      });

      // Refresh contacts list
      await fetchContacts();

      // Close modal after a brief delay
      setTimeout(() => {
        setShowAddModal(false);
        setFormSuccess("");
      }, 1500);
    } catch (err) {
      setFormError(err.message || "Failed to add customer");
    } finally {
      setSubmitting(false);
    }
  };

  const closeAddModal = () => {
    setShowAddModal(false);
    setFormData({
      firstName: "",
      lastName: "",
      email: "",
      phone: "",
      notes: "",
    });
    setFormError("");
    setFormSuccess("");
  };

  // Filter contacts based on search
  const filteredContacts = contacts.filter((contact) => {
    const searchLower = searchTerm.toLowerCase();
    return (
      contact.firstName?.toLowerCase().includes(searchLower) ||
      contact.lastName?.toLowerCase().includes(searchLower) ||
      contact.email?.toLowerCase().includes(searchLower) ||
      contact.phone?.includes(searchTerm)
    );
  });

  // Get initials for avatar
  const getInitials = (contact) => {
    const first = contact.firstName?.[0] || "";
    const last = contact.lastName?.[0] || "";
    return (first + last).toUpperCase() || "?";
  };

  // Get random color based on name (consistent per contact)
  const getAvatarColor = (contact) => {
    const colors = [
      "#6366f1",
      "#8b5cf6",
      "#a855f7",
      "#d946ef",
      "#ec4899",
      "#f43f5e",
      "#ef4444",
      "#f97316",
      "#f59e0b",
      "#eab308",
      "#84cc16",
      "#22c55e",
      "#10b981",
      "#14b8a6",
      "#06b6d4",
      "#0ea5e9",
      "#3b82f6",
      "#6366f1",
    ];
    const nameSum = (contact.firstName + contact.lastName)
      .split("")
      .reduce((acc, char) => acc + char.charCodeAt(0), 0);
    return colors[nameSum % colors.length];
  };

  // Format date
  const formatDate = (dateString) => {
    if (!dateString) return "-";
    return new Date(dateString).toLocaleDateString("en-US", {
      month: "short",
      day: "numeric",
      year: "numeric",
    });
  };

  // Styles
  const styles = {
    container: {
      padding: "20px",
      maxWidth: "1400px",
      margin: "0 auto",
    },
    header: {
      marginBottom: "24px",
    },
    headerRow: {
      display: "flex",
      justifyContent: "space-between",
      alignItems: "flex-start",
      gap: "16px",
      flexWrap: "wrap",
    },
    title: {
      margin: "0 0 8px 0",
      fontSize: "clamp(24px, 5vw, 32px)",
      fontWeight: "700",
      color: "#111827",
    },
    subtitle: {
      margin: 0,
      color: "#6b7280",
      fontSize: "14px",
    },
    importButton: {
      padding: "12px 20px",
      backgroundColor: "#0ea5e9",
      color: "#fff",
      border: "none",
      borderRadius: "8px",
      fontSize: "14px",
      fontWeight: "600",
      cursor: "pointer",
      display: "flex",
      alignItems: "center",
      gap: "8px",
      whiteSpace: "nowrap",
    },
    toolbar: {
      display: "flex",
      flexWrap: "wrap",
      gap: "12px",
      marginBottom: "24px",
      alignItems: "center",
    },
    searchContainer: {
      flex: "1 1 300px",
      minWidth: "200px",
      position: "relative",
    },
    searchInput: {
      width: "100%",
      padding: "12px 16px 12px 44px",
      border: "1px solid #e5e7eb",
      borderRadius: "12px",
      fontSize: "14px",
      outline: "none",
      transition: "border-color 0.2s, box-shadow 0.2s",
      boxSizing: "border-box",
    },
    searchIcon: {
      position: "absolute",
      left: "16px",
      top: "50%",
      transform: "translateY(-50%)",
      color: "#9ca3af",
      fontSize: "16px",
    },
    viewToggle: {
      display: "flex",
      background: "#f3f4f6",
      borderRadius: "8px",
      padding: "4px",
    },
    viewButton: (active) => ({
      padding: "8px 16px",
      border: "none",
      borderRadius: "6px",
      background: active ? "white" : "transparent",
      color: active ? "#111827" : "#6b7280",
      fontSize: "14px",
      fontWeight: "500",
      cursor: "pointer",
      transition: "all 0.2s",
      boxShadow: active ? "0 1px 3px rgba(0,0,0,0.1)" : "none",
    }),
    statsBar: {
      display: "flex",
      gap: "16px",
      marginBottom: "24px",
      flexWrap: "wrap",
    },
    statCard: {
      background: "white",
      border: "1px solid #e5e7eb",
      borderRadius: "12px",
      padding: "16px 24px",
      flex: "1 1 150px",
      minWidth: "120px",
    },
    statValue: {
      fontSize: "28px",
      fontWeight: "700",
      color: "#111827",
      margin: 0,
    },
    statLabel: {
      fontSize: "13px",
      color: "#6b7280",
      margin: "4px 0 0 0",
    },
    grid: {
      display: "grid",
      gridTemplateColumns: "repeat(auto-fill, minmax(280px, 1fr))",
      gap: "16px",
    },
    card: {
      background: "white",
      border: "1px solid #e5e7eb",
      borderRadius: "16px",
      padding: "20px",
      cursor: "pointer",
      transition: "all 0.2s",
      ":hover": {
        borderColor: "#6366f1",
        boxShadow: "0 4px 12px rgba(99, 102, 241, 0.15)",
      },
    },
    cardHeader: {
      display: "flex",
      alignItems: "center",
      gap: "14px",
      marginBottom: "16px",
    },
    avatar: (color) => ({
      width: "48px",
      height: "48px",
      borderRadius: "50%",
      background: color,
      color: "white",
      display: "flex",
      alignItems: "center",
      justifyContent: "center",
      fontSize: "18px",
      fontWeight: "600",
      flexShrink: 0,
    }),
    cardName: {
      margin: 0,
      fontSize: "16px",
      fontWeight: "600",
      color: "#111827",
    },
    cardSource: {
      margin: "2px 0 0 0",
      fontSize: "12px",
      color: "#9ca3af",
      textTransform: "capitalize",
    },
    cardDetails: {
      display: "flex",
      flexDirection: "column",
      gap: "8px",
    },
    cardDetail: {
      display: "flex",
      alignItems: "center",
      gap: "8px",
      fontSize: "13px",
      color: "#4b5563",
    },
    cardIcon: {
      width: "16px",
      textAlign: "center",
      color: "#9ca3af",
    },
    listContainer: {
      background: "white",
      border: "1px solid #e5e7eb",
      borderRadius: "16px",
      overflow: "hidden",
    },
    listHeader: {
      display: "grid",
      gridTemplateColumns: "1fr 1fr 1fr auto",
      padding: "14px 20px",
      background: "#f9fafb",
      borderBottom: "1px solid #e5e7eb",
      fontSize: "12px",
      fontWeight: "600",
      color: "#6b7280",
      textTransform: "uppercase",
      letterSpacing: "0.05em",
    },
    listRow: {
      display: "grid",
      gridTemplateColumns: "1fr 1fr 1fr auto",
      padding: "14px 20px",
      borderBottom: "1px solid #f3f4f6",
      alignItems: "center",
      cursor: "pointer",
      transition: "background 0.2s",
    },
    listName: {
      display: "flex",
      alignItems: "center",
      gap: "12px",
    },
    listAvatar: (color) => ({
      width: "36px",
      height: "36px",
      borderRadius: "50%",
      background: color,
      color: "white",
      display: "flex",
      alignItems: "center",
      justifyContent: "center",
      fontSize: "14px",
      fontWeight: "600",
      flexShrink: 0,
    }),
    modal: {
      position: "fixed",
      top: 0,
      left: 0,
      right: 0,
      bottom: 0,
      background: "rgba(0, 0, 0, 0.5)",
      display: "flex",
      alignItems: "center",
      justifyContent: "center",
      padding: "20px",
      zIndex: 1000,
    },
    modalContent: {
      background: "white",
      borderRadius: "20px",
      width: "100%",
      maxWidth: "500px",
      maxHeight: "90vh",
      overflow: "auto",
    },
    modalHeader: {
      padding: "24px 24px 0",
      display: "flex",
      alignItems: "flex-start",
      gap: "16px",
    },
    modalAvatar: (color) => ({
      width: "64px",
      height: "64px",
      borderRadius: "50%",
      background: color,
      color: "white",
      display: "flex",
      alignItems: "center",
      justifyContent: "center",
      fontSize: "24px",
      fontWeight: "600",
      flexShrink: 0,
    }),
    modalName: {
      margin: "0 0 4px 0",
      fontSize: "22px",
      fontWeight: "700",
      color: "#111827",
    },
    modalSubtitle: {
      margin: 0,
      fontSize: "14px",
      color: "#6b7280",
    },
    modalClose: {
      position: "absolute",
      top: "16px",
      right: "16px",
      background: "#f3f4f6",
      border: "none",
      borderRadius: "50%",
      width: "36px",
      height: "36px",
      cursor: "pointer",
      fontSize: "20px",
      color: "#6b7280",
      display: "flex",
      alignItems: "center",
      justifyContent: "center",
    },
    modalBody: {
      padding: "24px",
    },
    modalSection: {
      marginBottom: "20px",
    },
    modalLabel: {
      fontSize: "12px",
      fontWeight: "600",
      color: "#6b7280",
      textTransform: "uppercase",
      letterSpacing: "0.05em",
      marginBottom: "8px",
    },
    modalValue: {
      fontSize: "15px",
      color: "#111827",
      display: "flex",
      alignItems: "center",
      gap: "10px",
    },
    modalLink: {
      color: "#6366f1",
      textDecoration: "none",
    },
    emptyState: {
      textAlign: "center",
      padding: "60px 20px",
    },
    emptyIcon: {
      fontSize: "64px",
      marginBottom: "16px",
    },
    emptyTitle: {
      fontSize: "20px",
      fontWeight: "600",
      color: "#111827",
      margin: "0 0 8px 0",
    },
    emptyText: {
      color: "#6b7280",
      margin: 0,
    },
    loadingContainer: {
      display: "flex",
      justifyContent: "center",
      alignItems: "center",
      padding: "80px 20px",
    },
    spinner: {
      width: "40px",
      height: "40px",
      border: "3px solid #e5e7eb",
      borderTopColor: "#6366f1",
      borderRadius: "50%",
      animation: "spin 1s linear infinite",
    },
    errorBox: {
      background: "#fef2f2",
      border: "1px solid #fecaca",
      borderRadius: "12px",
      padding: "16px 20px",
      display: "flex",
      alignItems: "center",
      gap: "12px",
      marginBottom: "24px",
    },
    errorText: {
      color: "#dc2626",
      fontSize: "14px",
      margin: 0,
    },
    mobileListRow: {
      display: "flex",
      alignItems: "center",
      padding: "14px 16px",
      borderBottom: "1px solid #f3f4f6",
      gap: "12px",
    },
    mobileListInfo: {
      flex: 1,
      minWidth: 0,
    },
    mobileListName: {
      fontWeight: "600",
      fontSize: "15px",
      color: "#111827",
      margin: 0,
      whiteSpace: "nowrap",
      overflow: "hidden",
      textOverflow: "ellipsis",
    },
    mobileListDetail: {
      fontSize: "13px",
      color: "#6b7280",
      margin: "2px 0 0 0",
      whiteSpace: "nowrap",
      overflow: "hidden",
      textOverflow: "ellipsis",
    },
  };

  // Add CSS keyframes for spinner
  useEffect(() => {
    const styleSheet = document.createElement("style");
    styleSheet.textContent = `
      @keyframes spin {
        to { transform: rotate(360deg); }
      }
      .contact-card:hover {
        border-color: #6366f1 !important;
        box-shadow: 0 4px 12px rgba(99, 102, 241, 0.15) !important;
      }
      .contact-row:hover {
        background: #f9fafb !important;
      }
      .search-input:focus {
        border-color: #6366f1 !important;
        box-shadow: 0 0 0 3px rgba(99, 102, 241, 0.1) !important;
      }
    `;
    document.head.appendChild(styleSheet);
    return () => document.head.removeChild(styleSheet);
  }, []);

  // Check if mobile
  const [isMobile, setIsMobile] = useState(window.innerWidth < 768);
  useEffect(() => {
    const handleResize = () => setIsMobile(window.innerWidth < 768);
    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, []);

  return (
    <LayoutComponent>
      <div className="customers-page" style={styles.container}>
        {/* Header */}
        <div style={styles.header}>
          <div style={styles.headerRow}>
            <div>
              <h1 style={styles.title}>Contacts</h1>
              <p style={styles.subtitle}>
                Manage your customer contacts and address book
              </p>
            </div>
            <div style={{ display: "flex", gap: "12px" }}>
              <button
                onClick={() => setShowAddModal(true)}
                style={{
                  ...styles.importButton,
                  backgroundColor: "#10b981",
                }}
              >
                ➕ Add Customer
              </button>
              <button
                onClick={() =>
                  navigate(
                    isBoothRenterPortal
                      ? "/dashboard/booth-renter/customers/import"
                      : isFreelancerPortal
                        ? "/dashboard/freelancer/customers/import"
                        : isProfessionalPortal
                          ? "/dashboard/professional/customers/import"
                          : "/dashboard/customers/import",
                  )
                }
                style={styles.importButton}
              >
                📥 Import Customers
              </button>
            </div>
          </div>
        </div>

        {/* Stats Bar */}
        <div style={styles.statsBar}>
          <div style={styles.statCard}>
            <p style={styles.statValue}>{contacts.length}</p>
            <p style={styles.statLabel}>Total Contacts</p>
          </div>
          <div style={styles.statCard}>
            <p style={{ ...styles.statValue, color: "#10b981" }}>
              {contacts.filter((c) => c.email).length}
            </p>
            <p style={styles.statLabel}>With Email</p>
          </div>
          <div style={styles.statCard}>
            <p style={{ ...styles.statValue, color: "#6366f1" }}>
              {contacts.filter((c) => c.phone).length}
            </p>
            <p style={styles.statLabel}>With Phone</p>
          </div>
        </div>

        {/* Toolbar */}
        <div style={styles.toolbar}>
          <div style={styles.searchContainer}>
            <span style={styles.searchIcon}>🔍</span>
            <input
              className="search-input"
              type="text"
              placeholder="Search contacts..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              style={styles.searchInput}
            />
          </div>

          {!isMobile && (
            <div style={styles.viewToggle}>
              <button
                style={styles.viewButton(viewMode === "grid")}
                onClick={() => setViewMode("grid")}
              >
                ▦ Grid
              </button>
              <button
                style={styles.viewButton(viewMode === "list")}
                onClick={() => setViewMode("list")}
              >
                ☰ List
              </button>
            </div>
          )}
        </div>

        {/* Error */}
        {error && (
          <div style={styles.errorBox}>
            <span>⚠️</span>
            <p style={styles.errorText}>{error}</p>
          </div>
        )}

        {/* Loading */}
        {loading && (
          <div style={styles.loadingContainer}>
            <div style={styles.spinner} />
          </div>
        )}

        {/* Empty State */}
        {!loading && filteredContacts.length === 0 && (
          <div style={styles.emptyState}>
            <div style={styles.emptyIcon}>📇</div>
            <h3 style={styles.emptyTitle}>
              {searchTerm ? "No contacts found" : "No contacts yet"}
            </h3>
            <p style={styles.emptyText}>
              {searchTerm
                ? "Try adjusting your search terms"
                : "Add contacts manually or import them via the Migration Wizard"}
            </p>
          </div>
        )}

        {/* Grid View */}
        {!loading &&
          filteredContacts.length > 0 &&
          (viewMode === "grid" || isMobile) &&
          !isMobile && (
            <div style={styles.grid}>
              {filteredContacts.map((contact) => (
                <div
                  key={contact._id}
                  className="contact-card"
                  style={styles.card}
                  onClick={() => setSelectedContact(contact)}
                >
                  <div style={styles.cardHeader}>
                    <div style={styles.avatar(getAvatarColor(contact))}>
                      {getInitials(contact)}
                    </div>
                    <div>
                      <h3 style={styles.cardName}>
                        {contact.firstName} {contact.lastName}
                      </h3>
                      <p style={styles.cardSource}>
                        {contact.source?.replace("_", " ") || "Manual"}
                      </p>
                    </div>
                  </div>
                  <div style={styles.cardDetails}>
                    {contact.email && (
                      <div style={styles.cardDetail}>
                        <span style={styles.cardIcon}>✉️</span>
                        <span
                          style={{
                            overflow: "hidden",
                            textOverflow: "ellipsis",
                          }}
                        >
                          {contact.email}
                        </span>
                      </div>
                    )}
                    {contact.phone && (
                      <div style={styles.cardDetail}>
                        <span style={styles.cardIcon}>📱</span>
                        {contact.phone}
                      </div>
                    )}
                    {contact.notes && (
                      <div style={styles.cardDetail}>
                        <span style={styles.cardIcon}>📝</span>
                        <span
                          style={{
                            overflow: "hidden",
                            textOverflow: "ellipsis",
                            whiteSpace: "nowrap",
                          }}
                        >
                          {contact.notes}
                        </span>
                      </div>
                    )}
                  </div>
                </div>
              ))}
            </div>
          )}

        {/* List View (Desktop) */}
        {!loading &&
          filteredContacts.length > 0 &&
          viewMode === "list" &&
          !isMobile && (
            <div style={styles.listContainer}>
              <div style={styles.listHeader}>
                <span>Name</span>
                <span>Email</span>
                <span>Phone</span>
                <span>Added</span>
              </div>
              {filteredContacts.map((contact) => (
                <div
                  key={contact._id}
                  className="contact-row"
                  style={styles.listRow}
                  onClick={() => setSelectedContact(contact)}
                >
                  <div style={styles.listName}>
                    <div style={styles.listAvatar(getAvatarColor(contact))}>
                      {getInitials(contact)}
                    </div>
                    <span style={{ fontWeight: "500" }}>
                      {contact.firstName} {contact.lastName}
                    </span>
                  </div>
                  <span style={{ color: "#4b5563", fontSize: "14px" }}>
                    {contact.email || "-"}
                  </span>
                  <span style={{ color: "#4b5563", fontSize: "14px" }}>
                    {contact.phone || "-"}
                  </span>
                  <span style={{ color: "#9ca3af", fontSize: "13px" }}>
                    {formatDate(contact.createdAt)}
                  </span>
                </div>
              ))}
            </div>
          )}

        {/* Mobile List View */}
        {!loading && filteredContacts.length > 0 && isMobile && (
          <div style={styles.listContainer}>
            {filteredContacts.map((contact) => (
              <div
                key={contact._id}
                className="contact-row"
                style={styles.mobileListRow}
                onClick={() => setSelectedContact(contact)}
              >
                <div style={styles.listAvatar(getAvatarColor(contact))}>
                  {getInitials(contact)}
                </div>
                <div style={styles.mobileListInfo}>
                  <p style={styles.mobileListName}>
                    {contact.firstName} {contact.lastName}
                  </p>
                  <p style={styles.mobileListDetail}>
                    {contact.email || contact.phone || "No contact info"}
                  </p>
                </div>
                <span style={{ color: "#9ca3af", fontSize: "20px" }}>›</span>
              </div>
            ))}
          </div>
        )}

        {/* Contact Detail Modal */}
        {selectedContact && (
          <div style={styles.modal} onClick={() => setSelectedContact(null)}>
            <div
              style={{ ...styles.modalContent, position: "relative" }}
              onClick={(e) => e.stopPropagation()}
            >
              <button
                style={styles.modalClose}
                onClick={() => setSelectedContact(null)}
              >
                ✕
              </button>

              <div style={styles.modalHeader}>
                <div
                  style={styles.modalAvatar(getAvatarColor(selectedContact))}
                >
                  {getInitials(selectedContact)}
                </div>
                <div>
                  <h2 style={styles.modalName}>
                    {selectedContact.firstName} {selectedContact.lastName}
                  </h2>
                  <p style={styles.modalSubtitle}>
                    Added {formatDate(selectedContact.createdAt)}
                  </p>
                </div>
              </div>

              <div style={styles.modalBody}>
                {selectedContact.email && (
                  <div style={styles.modalSection}>
                    <div style={styles.modalLabel}>Email</div>
                    <div style={styles.modalValue}>
                      <span>✉️</span>
                      <a
                        href={`mailto:${selectedContact.email}`}
                        style={styles.modalLink}
                      >
                        {selectedContact.email}
                      </a>
                    </div>
                  </div>
                )}

                {selectedContact.phone && (
                  <div style={styles.modalSection}>
                    <div style={styles.modalLabel}>Phone</div>
                    <div style={styles.modalValue}>
                      <span>📱</span>
                      <a
                        href={`tel:${selectedContact.phone}`}
                        style={styles.modalLink}
                      >
                        {selectedContact.phone}
                      </a>
                    </div>
                  </div>
                )}

                {selectedContact.notes && (
                  <div style={styles.modalSection}>
                    <div style={styles.modalLabel}>Notes</div>
                    <div style={styles.modalValue}>
                      <span>📝</span>
                      <span>{selectedContact.notes}</span>
                    </div>
                  </div>
                )}

                <div style={styles.modalSection}>
                  <div style={styles.modalLabel}>Source</div>
                  <div style={styles.modalValue}>
                    <span>📥</span>
                    <span style={{ textTransform: "capitalize" }}>
                      {selectedContact.source?.replace("_", " ") ||
                        "Manual Entry"}
                    </span>
                  </div>
                </div>

                {selectedContact.lastContactDate && (
                  <div style={styles.modalSection}>
                    <div style={styles.modalLabel}>Last Contact</div>
                    <div style={styles.modalValue}>
                      <span>📅</span>
                      <span>{formatDate(selectedContact.lastContactDate)}</span>
                    </div>
                  </div>
                )}
              </div>
            </div>
          </div>
        )}

        {/* Add Customer Modal */}
        {showAddModal && (
          <div
            style={styles.modal}
            onClick={(e) => e.target === e.currentTarget && closeAddModal()}
          >
            <div style={{ ...styles.modalContent, maxWidth: "500px" }}>
              <div style={styles.modalHeader}>
                <h2 style={{ margin: 0, fontSize: "20px", fontWeight: "600" }}>
                  Add New Customer
                </h2>
                <button
                  onClick={closeAddModal}
                  style={{
                    background: "none",
                    border: "none",
                    fontSize: "24px",
                    cursor: "pointer",
                    color: "#9ca3af",
                    padding: "0",
                    lineHeight: "1",
                  }}
                >
                  ×
                </button>
              </div>

              <form onSubmit={handleAddCustomer} style={{ padding: "24px" }}>
                {formError && (
                  <div
                    style={{
                      padding: "12px 16px",
                      backgroundColor: "#fee2e2",
                      color: "#dc2626",
                      borderRadius: "8px",
                      marginBottom: "16px",
                      fontSize: "14px",
                    }}
                  >
                    {formError}
                  </div>
                )}

                {formSuccess && (
                  <div
                    style={{
                      padding: "12px 16px",
                      backgroundColor: "#d1fae5",
                      color: "#065f46",
                      borderRadius: "8px",
                      marginBottom: "16px",
                      fontSize: "14px",
                    }}
                  >
                    {formSuccess}
                  </div>
                )}

                <div
                  style={{
                    display: "flex",
                    flexDirection: "column",
                    gap: "16px",
                  }}
                >
                  <div>
                    <label
                      style={{
                        display: "block",
                        marginBottom: "6px",
                        fontSize: "14px",
                        fontWeight: "500",
                        color: "#374151",
                      }}
                    >
                      First Name <span style={{ color: "#ef4444" }}>*</span>
                    </label>
                    <input
                      type="text"
                      name="firstName"
                      value={formData.firstName}
                      onChange={handleInputChange}
                      required
                      style={{
                        width: "100%",
                        padding: "10px 12px",
                        border: "1px solid #e5e7eb",
                        borderRadius: "8px",
                        fontSize: "14px",
                        outline: "none",
                        transition: "border-color 0.2s",
                        boxSizing: "border-box",
                      }}
                      onFocus={(e) => (e.target.style.borderColor = "#6366f1")}
                      onBlur={(e) => (e.target.style.borderColor = "#e5e7eb")}
                    />
                  </div>

                  <div>
                    <label
                      style={{
                        display: "block",
                        marginBottom: "6px",
                        fontSize: "14px",
                        fontWeight: "500",
                        color: "#374151",
                      }}
                    >
                      Last Name <span style={{ color: "#ef4444" }}>*</span>
                    </label>
                    <input
                      type="text"
                      name="lastName"
                      value={formData.lastName}
                      onChange={handleInputChange}
                      required
                      style={{
                        width: "100%",
                        padding: "10px 12px",
                        border: "1px solid #e5e7eb",
                        borderRadius: "8px",
                        fontSize: "14px",
                        outline: "none",
                        transition: "border-color 0.2s",
                        boxSizing: "border-box",
                      }}
                      onFocus={(e) => (e.target.style.borderColor = "#6366f1")}
                      onBlur={(e) => (e.target.style.borderColor = "#e5e7eb")}
                    />
                  </div>

                  <div>
                    <label
                      style={{
                        display: "block",
                        marginBottom: "6px",
                        fontSize: "14px",
                        fontWeight: "500",
                        color: "#374151",
                      }}
                    >
                      Email
                    </label>
                    <input
                      type="email"
                      name="email"
                      value={formData.email}
                      onChange={handleInputChange}
                      style={{
                        width: "100%",
                        padding: "10px 12px",
                        border: "1px solid #e5e7eb",
                        borderRadius: "8px",
                        fontSize: "14px",
                        outline: "none",
                        transition: "border-color 0.2s",
                        boxSizing: "border-box",
                      }}
                      onFocus={(e) => (e.target.style.borderColor = "#6366f1")}
                      onBlur={(e) => (e.target.style.borderColor = "#e5e7eb")}
                    />
                  </div>

                  <div>
                    <label
                      style={{
                        display: "block",
                        marginBottom: "6px",
                        fontSize: "14px",
                        fontWeight: "500",
                        color: "#374151",
                      }}
                    >
                      Phone
                    </label>
                    <input
                      type="tel"
                      name="phone"
                      value={formData.phone}
                      onChange={handleInputChange}
                      placeholder="(555) 123-4567"
                      style={{
                        width: "100%",
                        padding: "10px 12px",
                        border: "1px solid #e5e7eb",
                        borderRadius: "8px",
                        fontSize: "14px",
                        outline: "none",
                        transition: "border-color 0.2s",
                        boxSizing: "border-box",
                      }}
                      onFocus={(e) => (e.target.style.borderColor = "#6366f1")}
                      onBlur={(e) => (e.target.style.borderColor = "#e5e7eb")}
                    />
                  </div>

                  <div>
                    <label
                      style={{
                        display: "block",
                        marginBottom: "6px",
                        fontSize: "14px",
                        fontWeight: "500",
                        color: "#374151",
                      }}
                    >
                      Notes (Optional)
                    </label>
                    <textarea
                      name="notes"
                      value={formData.notes}
                      onChange={handleInputChange}
                      rows="3"
                      style={{
                        width: "100%",
                        padding: "10px 12px",
                        border: "1px solid #e5e7eb",
                        borderRadius: "8px",
                        fontSize: "14px",
                        outline: "none",
                        transition: "border-color 0.2s",
                        boxSizing: "border-box",
                        resize: "vertical",
                        fontFamily: "inherit",
                      }}
                      onFocus={(e) => (e.target.style.borderColor = "#6366f1")}
                      onBlur={(e) => (e.target.style.borderColor = "#e5e7eb")}
                    />
                  </div>

                  <div
                    style={{
                      fontSize: "13px",
                      color: "#6b7280",
                      fontStyle: "italic",
                    }}
                  >
                    * Either email or phone is required
                  </div>

                  <div
                    style={{ display: "flex", gap: "12px", marginTop: "8px" }}
                  >
                    <button
                      type="button"
                      onClick={closeAddModal}
                      disabled={submitting}
                      style={{
                        flex: 1,
                        padding: "12px",
                        border: "1px solid #e5e7eb",
                        borderRadius: "8px",
                        fontSize: "14px",
                        fontWeight: "600",
                        cursor: submitting ? "not-allowed" : "pointer",
                        backgroundColor: "white",
                        color: "#374151",
                        opacity: submitting ? 0.5 : 1,
                      }}
                    >
                      Cancel
                    </button>
                    <button
                      type="submit"
                      disabled={submitting}
                      style={{
                        flex: 1,
                        padding: "12px",
                        border: "none",
                        borderRadius: "8px",
                        fontSize: "14px",
                        fontWeight: "600",
                        cursor: submitting ? "not-allowed" : "pointer",
                        backgroundColor: submitting ? "#9ca3af" : "#10b981",
                        color: "white",
                      }}
                    >
                      {submitting ? "Adding..." : "Add Customer"}
                    </button>
                  </div>
                </div>
              </form>
            </div>
          </div>
        )}
      </div>
    </LayoutComponent>
  );
}

export default Contacts;
